import { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  type SelectChangeEvent
} from '@mui/material';

import { UpdateReferralStatusRequestCloseReasonEnum } from '@/types/crm-screener-api';

type CloseReason =
  (typeof UpdateReferralStatusRequestCloseReasonEnum)[keyof typeof UpdateReferralStatusRequestCloseReasonEnum];

const CLOSE_REASON_LABELS: Partial<Record<CloseReason, string>> = {
  declined: 'Declined',
  withdrawn: 'Withdrawn'
};

interface CloseReferralDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (reason: CloseReason) => void;
  isLoading: boolean;
  patientName: string;
}

export const CloseReferralDialog: React.FC<CloseReferralDialogProps> = ({
  open,
  onClose,
  onConfirm,
  isLoading,
  patientName
}) => {
  const [closeReason, setCloseReason] = useState<CloseReason | ''>('');

  useEffect(() => {
    if (!open) {
      setCloseReason('');
    }
  }, [open]);

  const handleReasonChange = (event: SelectChangeEvent) => {
    setCloseReason(event.target.value as CloseReason);
  };

  const handleConfirm = () => {
    if (closeReason) {
      onConfirm(closeReason);
    }
  };

  const handleClose = () => {
    setCloseReason('');
    onClose();
  };

  const patientLabel = patientName ? ` for ${patientName}` : '';

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>Close Referral</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Select a reason for closing this referral{patientLabel}.
        </DialogContentText>
        <FormControl fullWidth size="small">
          <InputLabel id="close-reason-label">Reason</InputLabel>
          <Select
            labelId="close-reason-label"
            value={closeReason}
            label="Reason"
            onChange={handleReasonChange}
          >
            {Object.entries(CLOSE_REASON_LABELS).map(([value, label]) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          loading={isLoading}
          disabled={!closeReason}
          variant="contained"
          color="error"
        >
          Close Referral
        </Button>
      </DialogActions>
    </Dialog>
  );
};
